// "Сценарий озвучки": the text the narrator reads, with ElevenLabs audio tags in [brackets].
// Children and parents never see it. Only tags may be added — the words must stay the same
// (checked by the server). A trial voicing of one paragraph costs ≈ its characters.
import { BEDTIME_TAGS, QUICK_TAGS, type AdminVersion } from '@fonus/shared';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Mic, RotateCcw, Save, Wand2 } from 'lucide-react';
import { Fragment, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api.ts';
import { humanFromApi } from '../lib/errors.ts';
import { credits, tashkent } from '../lib/format.ts';
import { cn, newKey } from '../lib/utils.ts';
import { AutoTextarea, JobStatusBadge } from './common.tsx';
import { ErrorCard } from './ErrorCard.tsx';
import { Badge } from './ui/badge.tsx';
import { Button } from './ui/button.tsx';

const TAG = /(\[[^[\]\n]{1,40}\])/g;
const ALLOWED = new Set<string>(BEDTIME_TAGS);
const isAllowed = (tag: string) => ALLOWED.has(tag.slice(1, -1).trim().toLowerCase());
export const stripTags = (s: string) =>
  s
    .replace(TAG, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +([,.!?;:…])/g, '$1')
    .split('\n')
    .map((l) => l.trim())
    .join('\n');
const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
const PREVIEW_MAX = 500;

/** The script with tags as coloured chips (unsupported ones in red — they will be removed). */
export function Highlighted({ script }: { script: string }) {
  return (
    <div className="whitespace-pre-wrap text-[15px] leading-relaxed" data-testid="script-preview">
      {script.split(TAG).map((part, i) =>
        i % 2 === 1 ? (
          <span
            key={i}
            className={cn(
              'mx-0.5 rounded-md px-1.5 py-0.5 text-[13px] font-semibold',
              isAllowed(part)
                ? 'bg-accent-soft text-accent-strong'
                : 'bg-bad/12 text-bad line-through',
            )}
          >
            {part}
          </span>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </div>
  );
}

export function ScriptEditor({
  storyId,
  v,
  editable,
  busy,
  onChanged,
}: {
  storyId: string;
  v: AdminVersion;
  editable: boolean;
  busy: boolean;
  onChanged: () => void;
}) {
  const saved = v.ttsScript ?? v.text;
  const [draft, setDraft] = useState(saved);
  const [para, setPara] = useState(0);
  const area = useRef<HTMLTextAreaElement>(null);
  const changed = draft !== saved;
  const wordsChanged = norm(stripTags(draft)) !== norm(v.text);
  const tags = draft.match(TAG) ?? [];
  const paragraphs = draft
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const fragment = paragraphs[Math.min(para, paragraphs.length - 1)] ?? '';
  const previewCost = Math.min(PREVIEW_MAX, fragment.length);
  const previewBusy = v.preview?.status === 'QUEUED' || v.preview?.status === 'RUNNING';

  const insert = (tag: string) => {
    const el = area.current;
    const at = el?.selectionStart ?? draft.length;
    const before = draft.slice(0, at);
    const pad = before && !/\s$/.test(before) ? ' ' : '';
    const next = `${before}${pad}[${tag}] ${draft.slice(at).replace(/^\s+/, '')}`;
    setDraft(next);
    requestAnimationFrame(() => {
      const pos = before.length + pad.length + tag.length + 3;
      el?.focus();
      el?.setSelectionRange(pos, pos);
    });
  };

  const save = useMutation({
    mutationFn: (script: string | null) =>
      api<{ removed: string[] }>(`/admin/stories/${storyId}/versions/${v.lang}/script`, {
        method: 'PUT',
        body: { script },
      }),
    onSuccess: (r, script) => {
      toast.success(
        script === null
          ? 'Сценарий сброшен — озвучка пойдёт по тексту'
          : r.removed.length
            ? `Сохранено, удалено неподдерживаемых тегов: ${String(r.removed.length)}`
            : 'Сценарий сохранён — переозвучьте, чтобы услышать',
      );
      onChanged();
    },
  });
  const direct = useMutation({
    mutationFn: () =>
      api(`/admin/stories/${storyId}/versions/${v.lang}/direct`, {
        method: 'POST',
        body: { idempotencyKey: newKey() },
      }),
    onSuccess: () => {
      toast.success('Claude расставляет теги — предложение появится здесь');
      onChanged();
    },
  });
  const preview = useMutation({
    mutationFn: () =>
      api(`/admin/stories/${storyId}/versions/${v.lang}/preview`, {
        method: 'POST',
        body: { idempotencyKey: newKey(), script: fragment },
      }),
    onSuccess: () => {
      toast.success('Пробная озвучка запущена');
      onChanged();
    },
  });
  const error = save.error ?? direct.error ?? preview.error;
  const sug = v.suggestion;
  const thinking = sug?.status === 'QUEUED' || sug?.status === 'RUNNING';
  const offer = sug?.status === 'SUCCEEDED' && sug.script && !sug.stale && sug.script !== draft;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[14px] text-muted">
        Это видит только диктор: дети и родители читают обычный текст. Можно только добавлять теги в
        квадратных скобках — слова должны остаться как в тексте. Не чаще одного тега на 2–3
        предложения.
      </p>
      {v.scriptWarnings.map((w) => (
        <Badge key={w} tone="warn" className="self-start">
          {w}
        </Badge>
      ))}
      {editable && (
        <div className="flex flex-wrap gap-1.5" aria-label="Вставить тег">
          {QUICK_TAGS.map((t) => (
            <button
              key={t}
              type="button"
              className="rounded-full border border-line px-2.5 py-1 text-[13px] font-semibold text-accent-strong hover:bg-accent-soft"
              onClick={() => insert(t)}
            >
              [{t}]
            </button>
          ))}
        </div>
      )}
      <AutoTextarea
        inputRef={area}
        aria-label="Сценарий озвучки"
        value={draft}
        disabled={!editable}
        onChange={(e) => setDraft(e.target.value)}
        className="font-mono text-[14px]"
      />
      <div className="rounded-[14px] bg-fill/60 p-3">
        <p className="mb-2 text-[12px] font-semibold text-muted uppercase">
          Как услышит диктор · тегов: {tags.length}
        </p>
        <Highlighted script={draft} />
      </div>
      {thinking && (
        <p className="flex items-center gap-2 text-[14px] text-muted">
          <Loader2 className="size-4 animate-spin" /> Claude расставляет теги…
        </p>
      )}
      {offer && sug.script && (
        <div className="flex flex-col gap-2 rounded-[14px] border border-accent/40 bg-accent-soft/40 p-3">
          <p className="text-[14px] font-semibold">Предложение Claude · тегов: {sug.tags}</p>
          <Highlighted script={sug.script} />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setDraft(sug.script ?? draft)}>
              Подставить в редактор
            </Button>
            <span className="self-center text-[12px] text-muted">
              Сказка не меняется, пока вы не нажмёте «Сохранить сценарий». Пробную озвучку можно
              сделать и до сохранения.
            </span>
          </div>
        </div>
      )}
      {wordsChanged && (
        <p className="text-[13px] text-bad">
          Слова отличаются от текста — так сохранить нельзя. Верните слова или нажмите «Сбросить».
        </p>
      )}
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!changed || wordsChanged || save.isPending}
            onClick={() => save.mutate(draft)}
          >
            <Save /> Сохранить сценарий
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || thinking || direct.isPending}
            onClick={() => direct.mutate()}
          >
            {direct.isPending ? <Loader2 className="animate-spin" /> : <Wand2 />} Расставить
            автоматически
          </Button>
          {v.ttsScript !== null && (
            <Button
              size="sm"
              variant="ghost"
              disabled={save.isPending}
              onClick={() => {
                setDraft(v.text);
                save.mutate(null);
              }}
            >
              <RotateCcw /> Сбросить
            </Button>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 rounded-[16px] border border-line p-4">
        <p className="font-semibold">Пробная озвучка</p>
        <p className="text-[14px] text-muted">
          Один абзац текущего сценария (до {PREVIEW_MAX} символов) — послушать теги и настройки
          голоса, прежде чем озвучивать всю сказку.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Абзац"
            className="h-9 rounded-[10px] border border-line bg-card px-2 text-[14px]"
            value={Math.min(para, Math.max(0, paragraphs.length - 1))}
            onChange={(e) => setPara(Number(e.target.value))}
          >
            {paragraphs.map((p, i) => (
              <option key={i} value={i}>
                Абзац {i + 1}: {stripTags(p).slice(0, 40)}…
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="secondary"
            disabled={!editable || !fragment || wordsChanged || previewBusy || preview.isPending}
            onClick={() => preview.mutate()}
          >
            {previewBusy ? <Loader2 className="animate-spin" /> : <Mic />} Озвучить абзац (≈
            {credits(previewCost)})
          </Button>
        </div>
        {v.preview && (
          <div className="flex flex-col gap-2 text-[14px]">
            <div className="flex flex-wrap items-center gap-2">
              <JobStatusBadge status={v.preview.status} />
              <span className="text-muted">
                {tashkent(v.preview.createdAt)}
                {v.preview.status === 'SUCCEEDED' && ` · списано ${credits(v.preview.credits)}`}
              </span>
            </div>
            {v.preview.url && (
              <audio controls preload="none" src={v.preview.url} className="w-full" />
            )}
            {v.preview.text && <Highlighted script={v.preview.text} />}
          </div>
        )}
      </div>
      {error && <ErrorCard error={humanFromApi(error)} />}
    </div>
  );
}
